/**
 * ThreadView — the open conversation in the Team dock (staff chat MVP, SR-14), ported from Antonin's catalog source
 * (Signal DS `catalog/src/components/TeamChat/TeamChat.tsx`, `ChatWindow` embedded + `Messages`): the header (face,
 * name, role), the "about" strip (patient switch only), the reconnecting strip, the messages and the composer.
 *
 * Messages, grouped the Facebook way: a run of one sender's messages shares one avatar (24, on the last); a centred
 * time line at the start and after an hour's gap; each bubble's time shows on hover (DS Tooltip); yours sit right in the
 * blue tint. Under your last message: "Sending…", "Sent" or "Seen" (the other person's live read, hub-status-store.ts).
 * A failed one says "Not sent · Retry"; Retry resends the same clientMessageId, so the server never stores it twice.
 * No messages yet: the face, the name and "Say hello. This is the start of your conversation."
 * Read: marked read on open, and again when a colleague's message arrives while it is open.
 * One-to-one only: no sender names, no "Seen by", no typing dots (not in the contract). Edit and delete are not in the
 * MVP: no UI for them; a message deleted elsewhere reads "Message deleted".
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { Avatar, Button, Tooltip, TooltipContent, TooltipTrigger } from '@scripsteam/scrips-signal-ds';
import { useOpenThreadStore } from '@/staff-chat/lib/open-thread-store';
import { useDockStore } from '@/staff-chat/lib/dock-store';
import { peerReadKey, useHubStatusStore } from '@/staff-chat/lib/hub-status-store';
import { isStaffChatPatientOn } from '@/staff-chat/lib/staff-chat-switch';
import { bubbleTime, differentDay, newClientMessageId, startsTimeLine, threadRole, threadTitle, timeLine } from '@/staff-chat/lib/chat-format';
import { messagesOldestFirst } from '@/staff-chat/realtime/staff-chat-cache';
import {
  useMarkStaffThreadRead,
  useSendStaffMessage,
  useStaffChatIdentity,
  useStaffThread,
  useThreadMessages,
} from '@/staff-chat/services/use-staff-chat';
import type { LocalStaffChatMessage } from '@/staff-chat/types/staff-chat';
import { staffChatErrorKind, NO_ACCESS_TITLE, UNAVAILABLE_TITLE } from '@/staff-chat/lib/staff-chat-errors';
import { PatientThreadCard } from './patient-thread-card';
import { Composer, Reconnecting, SkeletonLines } from './team-chat-parts';

export const THREAD_LOAD_ERROR = "Couldn't load the messages.";
export const START_LINE = 'Say hello. This is the start of your conversation.';

/** The line under your last message. */
function receiptOf(m: LocalStaffChatMessage, peerReadAt: string | undefined): string {
  if (m.sendState === 'sending') return 'Sending…';
  if (peerReadAt && Date.parse(peerReadAt) >= Date.parse(m.createdAt)) return 'Seen';
  return 'Sent';
}

/** A failed read: no access (403) and not available (503) say so with no Retry; anything else offers Retry. */
function LoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const kind = staffChatErrorKind(error);
  if (kind === 'no-access') return <p className="team-chat-note">{NO_ACCESS_TITLE}</p>;
  if (kind === 'unavailable') return <p className="team-chat-note">{UNAVAILABLE_TITLE}</p>;
  return (
    <p className="team-chat-note">
      {THREAD_LOAD_ERROR}{' '}
      <button type="button" className="team-chat-link" onClick={onRetry}>
        Retry
      </button>
    </p>
  );
}

export function ThreadView({ threadId }: { threadId: string }) {
  const { who, me } = useStaffChatIdentity();
  const thread = useStaffThread(threadId);
  const messages = useThreadMessages(threadId);
  const send = useSendStaffMessage(threadId);
  const markRead = useMarkStaffThreadRead();
  const peerReadAt = useHubStatusStore((s) => s.peerReadAt[peerReadKey(who, threadId)]);
  const reconnecting = useHubStatusStore((s) => s.status === 'reconnecting');
  const body = useRef<HTMLDivElement>(null);

  const list = messagesOldestFirst(messages.data);
  const newest = list.at(-1);
  // The newest message from a colleague: a new one arriving while the thread is open is read at once.
  const newestIncomingId = [...list].reverse().find((m) => m.senderUserId !== me && !m.sendState)?.id ?? null;
  // "Under your last message": the caller's newest message.
  const lastMineId = [...list].reverse().find((m) => m.senderUserId === me)?.id ?? null;

  // Synchronises with the live hub: tells it which thread is on screen, so a message here is not counted as unread.
  const setOpenThreadId = useOpenThreadStore((s) => s.setOpenThreadId);
  useEffect(() => {
    setOpenThreadId(threadId);
    return () => setOpenThreadId(null);
  }, [threadId, setOpenThreadId]);

  // Synchronises the server's read marker with what is on screen: on open, and on each new incoming message.
  // Once per open, after the messages are read (never on a failed read: 403 / 503 / 404), and once more for each new
  // colleague message while the thread is open. `lastMarked` keeps a re-read of the same messages from marking again.
  const { mutate: markThreadRead } = markRead;
  const lastMarked = useRef<string | null>(null);
  const readable = messages.isSuccess && !messages.isError && !thread.isError;
  useEffect(() => {
    if (!readable) return;
    const key = `${threadId}|${newestIncomingId ?? ''}`;
    if (lastMarked.current === key) return;
    lastMarked.current = key;
    markThreadRead(threadId);
  }, [readable, threadId, newestIncomingId, markThreadRead]);

  // The first message written in New message before this thread existed: sent once, here, with its client message id.
  const firstMessage = useDockStore((s) => (s.firstMessage?.threadId === threadId ? s.firstMessage : null));
  const takeFirstMessage = useDockStore((s) => s.takeFirstMessage);
  const { mutate: sendMessage } = send;
  // Once the messages have been read or have failed (403 / 503 / network): a failed read never strands it. The send
  // shows the message at once, and a refused send reads "Not sent · Retry" like any other.
  const messagesSettled = messages.isSuccess || messages.isError;
  useEffect(() => {
    if (!firstMessage || !messagesSettled) return;
    takeFirstMessage();
    sendMessage({ body: firstMessage.body, clientMessageId: firstMessage.clientMessageId });
  }, [firstMessage, messagesSettled, takeFirstMessage, sendMessage]);

  // Keeps the newest message in view (the DOM scroll position) when one is added.
  useLayoutEffect(() => {
    if (body.current) body.current.scrollTop = body.current.scrollHeight;
  }, [newest?.id, newest?.sendState]);

  const retry = (m: LocalStaffChatMessage) => {
    if (m.clientMessageId) send.mutate({ body: m.body, clientMessageId: m.clientMessageId });
  };

  const title = thread.data ? threadTitle(thread.data, me) : '';
  const role = thread.data ? threadRole(thread.data, me) : '';
  const patient = isStaffChatPatientOn() && thread.data?.kind === 'patient' ? thread.data.patient : null;

  let content;
  // A thread that could not be read still shows a message sent from here (its "Not sent · Retry" included).
  if (thread.isError && !thread.data && list.length === 0) content = <LoadError error={thread.error} onRetry={() => void thread.refetch()} />;
  else if (messages.isPending) content = <SkeletonLines label="Loading messages" />;
  else if (messages.isError && list.length === 0) content = <LoadError error={messages.error} onRetry={() => void messages.refetch()} />;
  else if (list.length === 0)
    content = (
      <div className="team-chat-start">
        <Avatar scale="compact" tone="provider" name={title} size={48} />
        <span className="team-chat-name">{title}</span>
        <p className="team-chat-note">{START_LINE}</p>
      </div>
    );
  else
    content = list.map((m, i) => {
      const prev = list[i - 1];
      const next = list[i + 1];
      const mine = m.senderUserId === me;
      const gap = startsTimeLine(prev?.createdAt, m.createdAt);
      const runStart = gap || prev?.senderUserId !== m.senderUserId;
      const runEnd = !next || next.senderUserId !== m.senderUserId || startsTimeLine(m.createdAt, next.createdAt);
      // The first line and a new day name the day ("Today 09:15"); a gap the same day is the time alone.
      const line = !prev || differentDay(prev.createdAt, m.createdAt) ? timeLine(m.createdAt) : bubbleTime(m.createdAt);
      const status = m.sendState;
      return (
        <div key={m.id} className="team-chat-msg">
          {gap ? <span className="team-chat-time-line">{line}</span> : null}
          <div className="team-chat-slot" data-mine={mine || undefined}>
            {!mine ? (
              <span className="team-chat-face">{runEnd ? <Avatar scale="compact" tone="provider" name={title} size={24} /> : null}</span>
            ) : null}
            <Tooltip>
              <TooltipTrigger
                render={
                  <p
                    className="team-chat-bubble"
                    data-message-id={m.id}
                    data-mine={mine || undefined}
                    data-run-start={runStart || undefined}
                    data-run-end={runEnd || undefined}
                    data-status={status}
                    dir="auto"
                    tabIndex={0}
                  />
                }
              >
                {m.isDeleted ? <span className="team-chat-deleted">Message deleted</span> : m.body}
              </TooltipTrigger>
              <TooltipContent>{bubbleTime(m.createdAt)}</TooltipContent>
            </Tooltip>
          </div>
          {mine && status === 'failed' ? (
            <span className="team-chat-failed" role="alert" data-slot="team-chat-receipt">
              Not sent ·{' '}
              <button type="button" className="team-chat-link" onClick={() => retry(m)}>
                Retry
              </button>
            </span>
          ) : null}
          {mine && m.id === lastMineId && status !== 'failed' ? (
            <span className="team-chat-receipt" data-slot="team-chat-receipt">
              {receiptOf(m, peerReadAt)}
            </span>
          ) : null}
        </div>
      );
    });

  return (
    <section className="team-chat-window" aria-label={title ? `Chat with ${title}` : 'Chat'} data-slot="team-chat-window">
      <header className="team-chat-head">
        {thread.data ? <Avatar scale="compact" tone="provider" name={title} size={32} /> : null}
        <span className="team-chat-who">
          <span className="team-chat-name">{title}</span>
          {role ? <span className="team-chat-meta">{role}</span> : null}
        </span>
      </header>
      {patient ? <PatientThreadCard patient={patient} /> : null}
      {reconnecting ? <Reconnecting /> : null}
      <div ref={body} className="team-chat-thread" data-slot="team-chat-thread" aria-live="polite">
        {messages.hasNextPage ? (
          <div className="team-chat-older">
            <Button type="button" variant="tertiary" size="sm" disabled={messages.isFetchingNextPage} onClick={() => void messages.fetchNextPage()}>
              Show older messages
            </Button>
          </div>
        ) : null}
        {content}
      </div>
      <Composer onSend={(text) => send.mutate({ body: text, clientMessageId: newClientMessageId() })} disabled={thread.isError && !thread.data} />
    </section>
  );
}
